import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MessageSquare,
  X,
  Send,
  ArrowLeft,
  Search,
  Paperclip,
  Hash,
  Trash2,
  Download,
  SmilePlus,
  Smile,
  Mic,
  Square,
  Play,
  Video,
  Pin,
  PinOff,
} from "lucide-react";
import dayjs from "dayjs";
import "../../lib/dates";
import { api } from "../../lib/api";
import { useAuthStore } from "../../store/authStore";
import useTenantWebSocket from "../../hooks/useTenantWebSocket";
import useVisualViewport from "../../hooks/useVisualViewport";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { UserAvatar } from "../users/UserAvatar";

/**
 * Slack-style in-app messaging widget mounted globally in `AppShell`.
 *
 *  - Floating bottom-right launcher with unread badge (DM + channel unread combined).
 *  - Panel: list of #channels (project group chats) + direct messages.
 *  - Two-pane feel: list ↔ thread. Thread supports text + file attachments.
 *  - Realtime via WebSocket `dm.new`, `channel.new`, and read receipts.
 *  - Bottom-right sonner toast when a new message arrives outside the active thread.
 */
export default function DmWidget() {
  const { user } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("list"); // 'list' | 'thread' | 'new'
  const [conversations, setConversations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [active, setActive] = useState(null); // { kind: 'dm'|'channel', id, name, email? }
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [pending, setPending] = useState([]); // attachments uploaded but not yet sent
  const [uploading, setUploading] = useState(false);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const scrollerRef = useRef(null);
  const fileRef = useRef(null);
  // Keep the active thread + view accessible from visibility / websocket
  // handlers without forcing them to re-subscribe every render.
  const activeRef = useRef(null);
  const viewRef = useRef("list");
  const openRef = useRef(false);
  useEffect(() => { activeRef.current = active; }, [active]);
  useEffect(() => { viewRef.current = view; }, [view]);
  useEffect(() => { openRef.current = open; }, [open]);

  // Mobile keyboard adaptation — on iOS / Android the on-screen keyboard
  // shrinks the visualViewport and can also shift its offsetTop (iOS
  // auto-scrolls the layout viewport to bring the focused input into
  // view). We pin the panel to the visualViewport rect on mobile.
  const {
    height: vvHeight,
    width: vvWidth,
    offsetTop: vvOffsetTop,
    offsetLeft: vvOffsetLeft,
  } = useVisualViewport();
  const panelRef = useRef(null);

  // Direct-DOM viewport tracking (mobile): React state updates lag behind
  // the iOS keyboard animation, so we also mutate the panel style
  // synchronously on every visualViewport event — the panel glides with
  // the keyboard instead of jumping after the animation ends.
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined" || window.innerWidth >= 640) return;
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const apply = () => {
      const el = panelRef.current;
      if (!el) return;
      el.style.top = `${vv.offsetTop}px`;
      el.style.left = `${vv.offsetLeft}px`;
      el.style.width = `${vv.width}px`;
      el.style.height = `${vv.height}px`;
    };
    const onChange = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    apply();
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
    };
  }, [open]);

  const totalUnread = useMemo(() => {
    const dm = conversations.reduce((a, c) => a + (c.unread || 0), 0);
    const ch = channels.reduce((a, c) => a + (c.unread || 0), 0);
    return dm + ch;
  }, [conversations, channels]);

  // Resolve any user id (including self) to {name, email, photo_url}. Used
  // by ConversationList / NewChatView / ThreadView to render avatars via
  // the shared UserAvatar component — keeps the widget consistent with
  // the rest of the app.
  const userLookup = useMemo(() => {
    const m = {};
    users.forEach((u) => (m[u.id] = u));
    if (user?.id) {
      m[user.id] = {
        id: user.id,
        name: user.name,
        email: user.email,
        photo_url: user.photo_url,
      };
    }
    return m;
  }, [users, user]);

  const loadLists = useCallback(async () => {
    try {
      const [cvs, chs] = await Promise.all([
        api.get("/dm/conversations"),
        api.get("/channels"),
      ]);
      setConversations(cvs.data || []);
      setChannels(chs.data || []);
    } catch {
      /* ignore */
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/users?assignable=true");
      setUsers(data.filter((u) => u.id !== user?.id));
    } catch {
      /* ignore */
    }
  }, [user?.id]);

  const scrollToBottom = () => {
    setTimeout(() => {
      scrollerRef.current?.scrollTo({
        top: scrollerRef.current.scrollHeight,
      });
    }, 30);
  };

  const openThread = useCallback(async (a) => {
    setActive(a);
    setView("thread");
    setMessages([]);
    try {
      if (a.kind === "dm") {
        const { data } = await api.get(`/dm/messages?peer_id=${a.id}`);
        setMessages(data.messages || []);
        await api.patch(`/dm/read?peer_id=${a.id}`).catch(() => {});
        setConversations((cur) =>
          cur.map((c) => (c.peer_id === a.id ? { ...c, unread: 0 } : c)),
        );
      } else {
        const { data } = await api.get(`/channels/${a.id}/messages`);
        setMessages(data.messages || []);
        await api.patch(`/channels/${a.id}/read`).catch(() => {});
        setChannels((cur) =>
          cur.map((c) => (c.channel_id === a.id ? { ...c, unread: 0 } : c)),
        );
      }
    } catch {
      setMessages([]);
    }
    scrollToBottom();
  }, []);

  // Silent refetch of the currently-open thread — does NOT clear existing
  // messages so we avoid a flash. Used when the tab regains visibility
  // (browser may have suspended the WebSocket) so mobile users don't have
  // to hard-refresh to see the reply they were just push-notified about.
  const reloadActiveThread = useCallback(async () => {
    const a = activeRef.current;
    if (!a || viewRef.current !== "thread" || !openRef.current) return;
    try {
      if (a.kind === "dm") {
        const { data } = await api.get(`/dm/messages?peer_id=${a.id}`);
        setMessages(data.messages || []);
        await api.patch(`/dm/read?peer_id=${a.id}`).catch(() => {});
      } else {
        const { data } = await api.get(`/channels/${a.id}/messages`);
        setMessages(data.messages || []);
        await api.patch(`/channels/${a.id}/read`).catch(() => {});
      }
      // Also refresh conversation / channel lists so unread badges flip.
      loadLists();
      scrollToBottom();
    } catch {
      /* ignore transient errors */
    }
  }, [loadLists]);

  // Whenever the browser tab regains focus / visibility we refetch the
  // active thread. Fixes the "push arrived but chat still shows stale
  // messages until refresh" bug on mobile when the WebSocket got
  // suspended during background.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible") reloadActiveThread();
    };
    window.addEventListener("focus", reloadActiveThread);
    document.addEventListener("visibilitychange", onVis);
    return () => {
      window.removeEventListener("focus", reloadActiveThread);
      document.removeEventListener("visibilitychange", onVis);
    };
  }, [reloadActiveThread]);

  // iOS Chrome / Safari PWA quirk: when an input inside a `position:fixed`
  // panel receives focus, the browser scrolls the LAYOUT viewport up to
  // bring the input into view, which drags fixed elements along with it
  // (WebKit bug). We defensively lock body scroll + snap `window.scrollY`
  // back to 0 whenever the mobile DM panel is open, which prevents the
  // panel from being yanked off-screen.
  useEffect(() => {
    if (!open) return;
    if (typeof window === "undefined") return;
    if (window.innerWidth >= 640) return; // desktop uses floating panel, no lock needed

    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyPosition = body.style.position;
    const prevBodyWidth = body.style.width;
    const prevBodyTop = body.style.top;
    const savedScrollY = window.scrollY;

    // Freeze the body in place at the current scroll offset. This
    // stops iOS from scrolling the underlying page when the keyboard
    // opens, so the fixed panel stays pinned to the viewport.
    html.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.top = `-${savedScrollY}px`;

    // On focus events (composer input), force scroll back to 0 as a
    // safety net for the rare iOS versions that still yank the layout.
    const forceScrollZero = () => {
      window.scrollTo(0, 0);
    };
    window.addEventListener("scroll", forceScrollZero, { passive: true });

    return () => {
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.position = prevBodyPosition;
      body.style.width = prevBodyWidth;
      body.style.top = prevBodyTop;
      // Restore the original scroll position on the underlying page.
      window.scrollTo(0, savedScrollY);
      window.removeEventListener("scroll", forceScrollZero);
    };
  }, [open]);

  // Keyboard open/close changes vvHeight — keep the thread scrolled to the
  // latest message so the composer + last messages stay visible.
  useEffect(() => {
    if (!open) return;
    if (viewRef.current === "thread") scrollToBottom();
  }, [open, vvHeight]);

  useEffect(() => {
    if (!open) return;
    loadLists();
    // Load tenant users so DM/conversation avatars can resolve to
    // photo_url via userLookup. Cheap call; users are cached client-side.
    loadUsers();
  }, [open, loadLists, loadUsers]);

  useEffect(() => {
    if (!user) return;
    loadLists();
  }, [user, loadLists]);

  // Cross-component nav: e.g. CommandPalette dispatches `dm-open` to jump
  // to a specific channel/DM thread.
  useEffect(() => {
    const handler = (e) => {
      const detail = e.detail;
      if (!detail?.kind || !detail?.id) return;
      setOpen(true);
      openThread(detail);
    };
    window.addEventListener("dm-open", handler);
    return () => window.removeEventListener("dm-open", handler);
  }, [openThread]);

  // Deep-link from a Web Push notification: URL like `/app/dashboard?dm_peer=<id>`
  // opens the widget on that peer's thread directly. We consume the param
  // once, then strip it from history so a refresh doesn't re-trigger.
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    const peerId = params.get("dm_peer");
    if (!peerId) return;
    setOpen(true);
    // We don't yet have the peer's name — pass an empty string; openThread
    // will resolve it from the conversation list once loaded.
    openThread({ kind: "dm", id: peerId, name: "", email: "" });
    params.delete("dm_peer");
    const cleaned = params.toString();
    const newUrl =
      window.location.pathname + (cleaned ? `?${cleaned}` : "") + window.location.hash;
    window.history.replaceState({}, "", newUrl);
  }, [user, openThread]);

  useTenantWebSocket((msg) => {
    if (!msg) return;

    if (msg.event === "dm.new") {
      if (msg.sender_id === user?.id) {
        loadLists();
        return;
      }
      loadLists();
      const inThread =
        open && view === "thread" && active?.kind === "dm" && active.id === msg.sender_id;
      if (inThread) {
        setMessages((cur) => [
          ...cur,
          {
            id: msg.id,
            sender_id: msg.sender_id,
            receiver_id: msg.receiver_id,
            body: msg.body,
            attachments: msg.attachments || [],
            created_at: msg.created_at,
            read_at: null,
          },
        ]);
        api.patch(`/dm/read?peer_id=${msg.sender_id}`).catch(() => {});
        scrollToBottom();
      } else {
        toast.message(msg.sender_name || "Yeni mesaj", {
          description: (msg.body || "📎 Dosya").slice(0, 120),
          duration: 6000,
          action: {
            label: "Aç",
            onClick: () => {
              setOpen(true);
              openThread({ kind: "dm", id: msg.sender_id, name: msg.sender_name || "", email: "" });
            },
          },
        });
      }
    } else if (msg.event === "channel.new") {
      if (msg.sender_id === user?.id) {
        loadLists();
        return;
      }
      loadLists();
      const inThread =
        open && view === "thread" && active?.kind === "channel" && active.id === msg.channel_id;
      if (inThread) {
        setMessages((cur) => [
          ...cur,
          {
            id: msg.id,
            sender_id: msg.sender_id,
            sender_name: msg.sender_name,
            body: msg.body,
            attachments: msg.attachments || [],
            created_at: msg.created_at,
          },
        ]);
        api.patch(`/channels/${msg.channel_id}/read`).catch(() => {});
        scrollToBottom();
      } else {
        toast.message(`#${msg.channel_name || "kanal"}`, {
          description: `${msg.sender_name || ""}: ${(msg.body || "📎 Dosya").slice(0, 100)}`,
          duration: 6000,
          action: {
            label: "Aç",
            onClick: () => {
              setOpen(true);
              openThread({ kind: "channel", id: msg.channel_id, name: msg.channel_name || "" });
            },
          },
        });
      }
    } else if (msg.event === "channel.pin") {
      setMessages((cur) =>
        cur.map((m) => (m.id === msg.message_id ? { ...m, pinned: msg.pinned } : m)),
      );
    } else if (msg.event === "dm.read" && active?.kind === "dm" && msg.peer_id === user?.id) {
      setMessages((cur) =>
        cur.map((m) =>
          m.sender_id === user.id && !m.read_at
            ? { ...m, read_at: new Date().toISOString() }
            : m,
        ),
      );
    } else if (msg.event === "dm.reaction" || msg.event === "channel.reaction") {
      setMessages((cur) =>
        cur.map((m) => (m.id === msg.message_id ? { ...m, reactions: msg.reactions } : m)),
      );
    }
  });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = ""; // reset so re-selecting same file works
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/attachments/messages", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPending((cur) => [...cur, data]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Yükleme başarısız");
    } finally {
      setUploading(false);
    }
  };

  // Upload a Blob/File directly and send it as its own message immediately
  // (used for voice recordings so the user doesn't need to press Send).
  const handleUploadBlob = async (file) => {
    if (!active) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data: att } = await api.post("/attachments/messages", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (active.kind === "dm") {
        const { data } = await api.post("/dm/messages", {
          peer_id: active.id,
          body: "",
          attachment_ids: [att.id],
        });
        setMessages((cur) => [...cur, data]);
      } else {
        const { data } = await api.post(`/channels/${active.id}/messages`, {
          body: "",
          attachment_ids: [att.id],
        });
        setMessages((cur) => [...cur, { ...data, sender_name: user?.name }]);
      }
      loadLists();
      setTimeout(() => {
        scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight });
      }, 30);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Ses gönderilemedi");
    }
  };

  const sendDraft = async () => {
    const body = draft.trim();
    if (!body && pending.length === 0) return;
    if (!active) return;
    const attachment_ids = pending.map((a) => a.id);
    setDraft("");
    setPending([]);
    try {
      if (active.kind === "dm") {
        const { data } = await api.post("/dm/messages", {
          peer_id: active.id,
          body,
          attachment_ids,
        });
        setMessages((cur) => [...cur, data]);
      } else {
        const { data } = await api.post(`/channels/${active.id}/messages`, {
          body,
          attachment_ids,
        });
        setMessages((cur) => [
          ...cur,
          { ...data, sender_name: user?.name },
        ]);
      }
      loadLists();
      scrollToBottom();
    } catch (err) {
      setDraft(body);
      setPending((cur) => [...cur, ...pending.filter((p) => !cur.find((x) => x.id === p.id))]);
      toast.error(err?.response?.data?.detail || "Mesaj gönderilemedi");
    }
  };

  const openNew = () => {
    setView("new");
    loadUsers();
  };

  // Build a deterministic Jitsi room name so both parties join the same room.
  const jitsiRoom = () => {
    if (!active) return null;
    const tid = user?.tenant_id || "t";
    if (active.kind === "channel") return `taskora-${tid}-ch-${active.id}`;
    const [a, b] = [user?.id, active.id].sort();
    return `taskora-${tid}-dm-${a}-${b}`;
  };

  const startVideoCall = async () => {
    if (!active) return;
    const room = jitsiRoom();
    const url = `https://meet.jit.si/${room}#config.prejoinPageEnabled=false`;
    // Open the Jitsi window immediately (must be triggered by user gesture).
    window.open(url, "_blank", "noopener,noreferrer");
    // Auto-send an invite message so the peer/channel members can click and join.
    const inviteBody = `📹 Görüntülü görüşme başlattı — Katıl: ${url}`;
    try {
      if (active.kind === "dm") {
        const { data } = await api.post("/dm/messages", {
          peer_id: active.id,
          body: inviteBody,
        });
        setMessages((cur) => [...cur, data]);
      } else {
        const { data } = await api.post(`/channels/${active.id}/messages`, {
          body: inviteBody,
        });
        setMessages((cur) => [...cur, { ...data, sender_name: user?.name }]);
      }
      loadLists();
    } catch (err) {
      toast.error("Davet mesajı gönderilemedi");
    }
  };

  const filteredUsers = useMemo(() => {
    const q = userQuery.toLowerCase();
    if (!q) return users;
    return users.filter(
      (u) =>
        (u.name && u.name.toLowerCase().includes(q)) ||
        (u.email && u.email.toLowerCase().includes(q)),
    );
  }, [users, userQuery]);

  if (!user) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="dm-launcher"
        aria-label="Mesajlar"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-zinc-900 text-white shadow-lg transition-transform hover:scale-105 hover:bg-zinc-800"
      >
        <MessageSquare className="h-6 w-6" strokeWidth={1.75} />
        {totalUnread > 0 && (
          <span
            data-testid="dm-unread-badge"
            className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white"
          >
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div
          ref={panelRef}
          data-testid="dm-panel"
          className="fixed z-40 flex flex-col overflow-hidden border-0 bg-white shadow-2xl sm:inset-auto sm:bottom-24 sm:right-6 sm:h-[560px] sm:w-[380px] sm:rounded-2xl sm:border sm:border-zinc-200"
          style={
            typeof window !== "undefined" && window.innerWidth < 640
              ? {
                  // Mobile: pin the panel to the visual viewport with an
                  // explicit top+height in JS. On iOS the keyboard OVERLAYS
                  // the layout viewport and shifts visualViewport.offsetTop,
                  // so top must track offsetTop (not 0) to stay glued to
                  // the visible area — otherwise the background bleeds
                  // through below the panel.
                  top: `${vvOffsetTop}px`,
                  left: `${vvOffsetLeft}px`,
                  width: `${vvWidth}px`,
                  height: `${vvHeight}px`,
                  // Home-indicator padding — becomes 0 when keyboard is
                  // open (iOS zeroes the bottom safe area then), so the
                  // composer flushes directly against the keyboard top.
                  paddingBottom: "env(safe-area-inset-bottom)",
                }
              : undefined
          }
        >
          <div
            className="flex items-center justify-between border-b border-zinc-200 bg-zinc-900 px-4 py-3 text-white"
            style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}
          >
            <div className="flex items-center gap-2">
              {view !== "list" && (
                <button
                  type="button"
                  onClick={() => setView("list")}
                  data-testid="dm-back"
                  className="rounded p-1 hover:bg-white/10"
                >
                  <ArrowLeft className="h-4 w-4" />
                </button>
              )}
              {view === "thread" && active?.kind === "dm" && (
                <UserAvatar
                  name={userLookup?.[active.id]?.name || active?.name}
                  email={userLookup?.[active.id]?.email || active?.email}
                  photoUrl={userLookup?.[active.id]?.photo_url}
                  size="sm"
                  className="ring-white/30"
                />
              )}
              <div>
                <div className="text-sm font-semibold">
                  {view === "list" && "Mesajlar"}
                  {view === "new" && "Yeni sohbet"}
                  {view === "thread" && (
                    active?.kind === "channel"
                      ? `# ${active.name}`
                      : active?.name || "Sohbet"
                  )}
                </div>
                {view === "thread" && active?.kind === "dm" && (
                  <div className="text-[11px] text-zinc-300">{active?.email}</div>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1">
              {view === "thread" && (
                <button
                  type="button"
                  onClick={startVideoCall}
                  data-testid="dm-videocall"
                  title="Görüntülü görüşme başlat (Jitsi Meet)"
                  className="rounded p-1.5 hover:bg-white/10"
                >
                  <Video className="h-4 w-4" />
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                data-testid="dm-close"
                className="rounded p-1 hover:bg-white/10"
                aria-label="Kapat"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {view === "list" && (
            <ListView
              channels={channels}
              conversations={conversations}
              onOpen={openThread}
              onNew={openNew}
              userLookup={userLookup}
            />
          )}

          {view === "new" && (
            <NewChatView
              users={filteredUsers}
              query={userQuery}
              onQuery={setUserQuery}
              onPick={(u) =>
                openThread({ kind: "dm", id: u.id, name: u.name, email: u.email })
              }
            />
          )}

          {view === "thread" && (
            <ThreadView
              messages={messages}
              me={user.id}
              activeKind={active?.kind}
              canPin={["super_admin", "ultra_admin"].includes(user.role) && active?.kind === "channel"}
              scrollerRef={scrollerRef}
              draft={draft}
              onDraft={setDraft}
              onSend={sendDraft}
              pending={pending}
              onRemovePending={(id) =>
                setPending((cur) => cur.filter((p) => p.id !== id))
              }
              onAttachClick={() => fileRef.current?.click()}
              onUploadBlob={handleUploadBlob}
              uploading={uploading}
              userLookup={userLookup}
            />
          )}
          <input
            ref={fileRef}
            type="file"
            hidden
            onChange={handleUpload}
            data-testid="dm-file-input"
          />
        </div>
      )}
    </>
  );
}

function ListView({ channels, conversations, onOpen, onNew, userLookup }) {
  return (
    <div className="flex-1 overflow-y-auto">
      <div className="border-b border-zinc-100 px-3 py-2">
        <Button
          type="button"
          size="sm"
          onClick={onNew}
          data-testid="dm-new-btn"
          className="w-full bg-zinc-900 text-white hover:bg-zinc-800"
        >
          Yeni sohbet başlat
        </Button>
      </div>
      {channels.length > 0 && (
        <>
          <SectionHeader label="Kanallar" count={channels.length} />
          <ul>
            {channels.map((c) => (
              <li key={c.channel_id}>
                <button
                  type="button"
                  onClick={() =>
                    onOpen({ kind: "channel", id: c.channel_id, name: c.name })
                  }
                  data-testid={`dm-channel-${c.channel_id}`}
                  className="flex w-full items-start gap-3 border-b border-zinc-50 px-3 py-2.5 text-left hover:bg-zinc-50"
                >
                  <Hash className="mt-1 h-4 w-4 shrink-0 text-zinc-400" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-zinc-900">
                        {c.name}
                      </span>
                      <span className="shrink-0 text-[10px] text-zinc-400">
                        {c.last_at ? dayjs(c.last_at).fromNow() : ""}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-zinc-500">
                        {c.last_from_me ? "Sen: " : ""}
                        {(c.last_body || "—").slice(0, 60)}
                      </span>
                      {c.unread > 0 && (
                        <span className="ml-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                          {c.unread}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
      <SectionHeader label="Direkt Mesajlar" count={conversations.length} />
      {conversations.length === 0 && (
        <div className="px-4 py-6 text-center text-xs text-zinc-500">
          Henüz DM yok.
        </div>
      )}
      <ul>
        {conversations.map((c) => (
          <li key={c.peer_id}>
            <button
              type="button"
              onClick={() =>
                onOpen({ kind: "dm", id: c.peer_id, name: c.peer_name, email: c.peer_email })
              }
              data-testid={`dm-conv-${c.peer_id}`}
              className="flex w-full items-start gap-3 border-b border-zinc-50 px-3 py-2.5 text-left hover:bg-zinc-50"
            >
              <UserAvatar
                name={userLookup?.[c.peer_id]?.name || c.peer_name}
                email={userLookup?.[c.peer_id]?.email || c.peer_email}
                photoUrl={userLookup?.[c.peer_id]?.photo_url}
                size="md"
              />
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-zinc-900">
                    {c.peer_name}
                  </span>
                  <span className="shrink-0 text-[10px] text-zinc-400">
                    {c.last_at ? dayjs(c.last_at).fromNow() : ""}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-zinc-500">
                    {c.last_from_me ? "Sen: " : ""}
                    {(c.last_body || "").slice(0, 60)}
                  </span>
                  {c.unread > 0 && (
                    <span className="ml-2 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
                      {c.unread}
                    </span>
                  )}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function SectionHeader({ label, count }) {
  return (
    <div className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
      {label} <span className="ml-1 text-zinc-400">{count}</span>
    </div>
  );
}

function NewChatView({ users, query, onQuery, onPick }) {
  return (
    <>
      <div className="border-b border-zinc-100 px-3 py-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Kişi ara..."
            data-testid="dm-user-search"
            className="pl-8 text-base sm:text-sm"
          />
        </div>
      </div>
      <ul className="flex-1 overflow-y-auto">
        {users.length === 0 && (
          <li className="px-4 py-12 text-center text-sm text-zinc-500">
            Kullanıcı bulunamadı.
          </li>
        )}
        {users.map((u) => (
          <li key={u.id}>
            <button
              type="button"
              onClick={() => onPick(u)}
              data-testid={`dm-user-${u.id}`}
              className="flex w-full items-center gap-3 border-b border-zinc-50 px-3 py-2.5 text-left hover:bg-zinc-50"
            >
              <UserAvatar
                name={u.name}
                email={u.email}
                photoUrl={u.photo_url}
                size="sm"
              />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-900">
                  {u.name}
                </div>
                <div className="truncate text-[11px] text-zinc-500">
                  {u.email}
                </div>
              </div>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function ThreadView({
  messages,
  me,
  activeKind,
  activeId,
  canPin,
  scrollerRef,
  draft,
  onDraft,
  onSend,
  pending,
  onRemovePending,
  onAttachClick,
  onUploadBlob,
  uploading,
  userLookup,
}) {
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const downloadUrl = (id) => `${backend}/api/attachments/${id}/download`;
  const isImage = (ct) => ct && ct.startsWith("image/");
  const isAudio = (ct) => ct && ct.startsWith("audio/");
  const draftRef = useRef(null);
  const [recording, setRecording] = useState(false);
  const recorderRef = useRef(null);
  const recStartRef = useRef(0);
  const [recSecs, setRecSecs] = useState(0);
  const timerRef = useRef(null);

  const toggleReaction = async (msg, emoji) => {
    const path =
      activeKind === "dm"
        ? `/dm/messages/${msg.id}/reactions`
        : `/channels/messages/${msg.id}/reactions`;
    try {
      await api.post(path, { emoji });
    } catch {
      /* ignore; ws will keep state right */
    }
  };

  const togglePin = async (msg) => {
    try {
      await api.patch(`/channels/messages/${msg.id}/pin`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sabitlenemedi");
    }
  };

  // Render pinned messages first (as a bookmark strip), then chronological order
  const pinnedMessages = messages.filter((m) => m.pinned);
  const restMessages = messages;

  const insertEmoji = (emoji) => {
    onDraft((draft || "") + emoji);
    setTimeout(() => draftRef.current?.focus(), 10);
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/webm";
      const rec = new MediaRecorder(stream, { mimeType: mime });
      const chunks = [];
      rec.ondataavailable = (e) => e.data.size > 0 && chunks.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(timerRef.current);
        setRecSecs(0);
        setRecording(false);
        const blob = new Blob(chunks, { type: "audio/webm" });
        const durSec = Math.max(1, Math.round((Date.now() - recStartRef.current) / 1000));
        const file = new File([blob], `voice-${Date.now()}.webm`, { type: "audio/webm" });
        await onUploadBlob(file, { duration: durSec });
      };
      recorderRef.current = rec;
      recStartRef.current = Date.now();
      rec.start();
      setRecording(true);
      timerRef.current = setInterval(() => {
        setRecSecs(Math.round((Date.now() - recStartRef.current) / 1000));
      }, 250);
    } catch (err) {
      toast.error("Mikrofona erişim reddedildi");
    }
  };

  const stopRecording = () => {
    try {
      recorderRef.current?.stop();
    } catch {
      setRecording(false);
    }
  };

  return (
    <>
      <div
        ref={scrollerRef}
        data-testid="dm-thread"
        className="flex-1 space-y-2 overflow-y-auto bg-zinc-50/40 px-3 py-3"
      >
        {activeKind === "channel" && pinnedMessages.length > 0 && (
          <div
            data-testid="pinned-strip"
            className="mb-2 rounded-lg border border-amber-200 bg-amber-50 p-2"
          >
            <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-800">
              <Pin className="h-3 w-3" /> Sabitlenmiş
            </div>
            <div className="space-y-1">
              {pinnedMessages.map((m) => (
                <div
                  key={`pin-${m.id}`}
                  data-testid={`pinned-${m.id}`}
                  className="rounded bg-white px-2 py-1 text-xs text-zinc-700 shadow-sm"
                >
                  <span className="font-medium">{m.sender_name || "?"}: </span>
                  <span className="text-zinc-600">{(m.body || "").slice(0, 200)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {messages.length === 0 && (
          <div className="mt-8 text-center text-xs text-zinc-500">
            {activeKind === "channel"
              ? "Bu kanal henüz sessiz. Konuşmayı başlat!"
              : "Hadi bir merhaba de."}
          </div>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === me;
          const sender = userLookup?.[m.sender_id] || {};
          const showChannelAvatar = activeKind === "channel" && !mine;
          return (
            <div
              key={m.id}
              data-testid={`dm-msg-${m.id}`}
              className={`group flex items-end gap-2 ${mine ? "justify-end" : "justify-start"}`}
            >
              {showChannelAvatar && (
                <UserAvatar
                  name={sender.name || m.sender_name}
                  email={sender.email}
                  photoUrl={sender.photo_url}
                  size="xs"
                />
              )}
              <div
                className={[
                  "relative max-w-[75%] whitespace-pre-wrap break-words rounded-2xl px-3 py-2 text-sm shadow-sm",
                  mine
                    ? "bg-zinc-900 text-white rounded-br-md"
                    : "bg-white text-zinc-900 rounded-bl-md border border-zinc-200",
                ].join(" ")}
              >
                {activeKind === "channel" && !mine && m.sender_name && (
                  <div className="mb-0.5 text-[10px] font-semibold text-zinc-500">
                    {m.sender_name}
                  </div>
                )}
                <MessageBody body={m.body} mine={mine} />
                {(m.attachments || []).map((att) => (
                  <div key={att.id} className="mt-1.5">
                    {isImage(att.content_type) ? (
                      <a href={downloadUrl(att.id)} target="_blank" rel="noreferrer">
                        <img
                          src={downloadUrl(att.id)}
                          alt={att.original_filename}
                          className="max-h-40 rounded-md border border-zinc-200"
                        />
                      </a>
                    ) : isAudio(att.content_type) ? (
                      <audio
                        controls
                        src={downloadUrl(att.id)}
                        className="w-56 max-w-full"
                        data-testid={`dm-audio-${att.id}`}
                      />
                    ) : (
                      <a
                        href={downloadUrl(att.id)}
                        target="_blank"
                        rel="noreferrer"
                        className={[
                          "flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs",
                          mine
                            ? "border-white/20 text-white hover:bg-white/10"
                            : "border-zinc-200 text-zinc-700 hover:bg-zinc-50",
                        ].join(" ")}
                      >
                        <Download className="h-3 w-3" />
                        <span className="truncate max-w-[180px]">
                          {att.original_filename}
                        </span>
                      </a>
                    )}
                  </div>
                ))}
                <div
                  className={`mt-0.5 text-[9px] ${
                    mine ? "text-zinc-400" : "text-zinc-400"
                  }`}
                >
                  {dayjs(m.created_at).format("HH:mm")}
                  {mine && activeKind === "dm" && m.read_at && " · ✓✓"}
                </div>
                {/* Reaction chips shown inside the bubble bottom */}
                {Object.entries(m.reactions || {}).filter(([, u]) => u?.length).length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {Object.entries(m.reactions || {})
                      .filter(([, users]) => users?.length)
                      .map(([emoji, users]) => {
                        const iReacted = users.includes(me);
                        return (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => toggleReaction(m, emoji)}
                            data-testid={`dm-reaction-${m.id}-${emoji}`}
                            className={[
                              "flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] transition-colors",
                              iReacted
                                ? "border-blue-400 bg-blue-50 text-blue-700"
                                : mine
                                  ? "border-white/20 bg-white/10 text-zinc-100"
                                  : "border-zinc-200 bg-white text-zinc-700 hover:bg-zinc-50",
                            ].join(" ")}
                          >
                            <span>{emoji}</span>
                            <span className="text-[10px]">{users.length}</span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>
              {/* WhatsApp-style small floating reaction button next to bubble */}
              <div
                className={[
                  "flex items-center gap-1 self-center opacity-0 group-hover:opacity-100 transition-opacity",
                  mine ? "order-last" : "order-first",
                ].join(" ")}
              >
                {canPin && (
                  <button
                    type="button"
                    onClick={() => togglePin(m)}
                    data-testid={`dm-pin-${m.id}`}
                    title={m.pinned ? "Sabitlemeyi kaldır" : "Duyuru olarak sabitle"}
                    className={[
                      "flex h-6 w-6 items-center justify-center rounded-full border shadow-sm",
                      m.pinned
                        ? "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200"
                        : "border-zinc-200 bg-white text-zinc-500 hover:bg-zinc-50",
                    ].join(" ")}
                  >
                    {m.pinned ? (
                      <PinOff className="h-3.5 w-3.5" />
                    ) : (
                      <Pin className="h-3.5 w-3.5" />
                    )}
                  </button>
                )}
                <ReactionAddButton msg={m} mine={mine} onToggle={(emoji) => toggleReaction(m, emoji)} />
              </div>
            </div>
          );
        })}
      </div>

      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1.5 border-t border-zinc-100 bg-zinc-50 px-3 py-2">
          {pending.map((p) => (
            <div
              key={p.id}
              data-testid={`dm-pending-${p.id}`}
              className="flex items-center gap-1.5 rounded-md border border-zinc-200 bg-white px-2 py-1 text-xs"
            >
              <Paperclip className="h-3 w-3 text-zinc-400" />
              <span className="max-w-[140px] truncate">{p.original_filename}</span>
              <button
                type="button"
                onClick={() => onRemovePending(p.id)}
                className="text-zinc-400 hover:text-red-500"
                aria-label="kaldır"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSend();
        }}
        className="border-t border-zinc-200 bg-white p-2"
      >
        {recording ? (
          <div
            data-testid="dm-recording-bar"
            className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-400 opacity-75" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-red-500" />
            </span>
            <span className="text-red-700">
              Kaydediliyor · {String(Math.floor(recSecs / 60)).padStart(2, "0")}:
              {String(recSecs % 60).padStart(2, "0")}
            </span>
            <button
              type="button"
              onClick={stopRecording}
              data-testid="dm-stop-record"
              className="ml-auto flex items-center gap-1 rounded-full bg-red-600 px-3 py-1 text-xs font-medium text-white hover:bg-red-700"
            >
              <Square className="h-3 w-3" />
              Bitir
            </button>
          </div>
        ) : (
          <div className="rounded-2xl border border-zinc-300 focus-within:border-zinc-500 focus-within:ring-1 focus-within:ring-zinc-500 bg-white px-3 pt-2 pb-1.5">
            <Textarea
              ref={draftRef}
              value={draft}
              onChange={(e) => onDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
              placeholder="Mesajınızı yazın..."
              rows={1}
              data-testid="dm-draft"
              className="min-h-[24px] max-h-32 resize-none border-0 p-0 text-base shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 sm:text-sm"
            />
            <div className="mt-1 flex items-center gap-0.5">
              <button
                type="button"
                onClick={onAttachClick}
                disabled={uploading}
                data-testid="dm-attach"
                className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100 disabled:opacity-50"
                title="Dosya ekle"
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    data-testid="dm-emoji-picker-btn"
                    className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100"
                    title="Emoji ekle"
                  >
                    <Smile className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent side="top" align="start" className="w-72 p-2">
                  <div className="mb-1 text-[10px] uppercase tracking-wider text-zinc-500">
                    Emoji
                  </div>
                  <div className="grid grid-cols-8 gap-1">
                    {COMPOSER_EMOJIS.map((e) => (
                      <button
                        key={e}
                        type="button"
                        onClick={() => insertEmoji(e)}
                        data-testid={`dm-emoji-${e}`}
                        className="rounded p-1 text-lg hover:bg-zinc-100"
                      >
                        {e}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
              <button
                type="button"
                onClick={startRecording}
                data-testid="dm-record"
                title="Sesli mesaj kaydet"
                className="rounded p-1.5 text-zinc-500 hover:bg-zinc-100"
              >
                <Mic className="h-4 w-4" />
              </button>
              <div className="ml-auto">
                <button
                  type="submit"
                  data-testid="dm-send"
                  disabled={!draft.trim() && pending.length === 0}
                  className={[
                    "flex h-8 w-8 items-center justify-center rounded-full transition-colors",
                    !draft.trim() && pending.length === 0
                      ? "bg-zinc-100 text-zinc-400"
                      : "bg-zinc-900 text-white hover:bg-zinc-800",
                  ].join(" ")}
                  title="Gönder"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </>
  );
}

const REACTION_SET = ["👍", "❤️", "🎉", "🚀", "✅", "👀", "🙌", "🔥"];

const URL_RE = /(https?:\/\/[^\s]+)/g;

/**
 * Render message body, converting URLs into styled links. Jitsi Meet URLs
 * get a special "📹 Görüşmeye katıl" button pill.
 */
function MessageBody({ body, mine }) {
  if (!body) return null;
  const parts = body.split(URL_RE);
  return (
    <>
      {parts.map((part, i) => {
        if (i % 2 === 1) {
          const isJitsi = part.startsWith("https://meet.jit.si/");
          if (isJitsi) {
            return (
              <a
                key={i}
                href={part}
                target="_blank"
                rel="noreferrer"
                data-testid="dm-join-videocall"
                className={[
                  "mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  mine
                    ? "bg-white/15 text-white hover:bg-white/25"
                    : "bg-emerald-100 text-emerald-800 hover:bg-emerald-200",
                ].join(" ")}
              >
                📹 Görüşmeye katıl
              </a>
            );
          }
          return (
            <a
              key={i}
              href={part}
              target="_blank"
              rel="noreferrer"
              className={mine ? "text-blue-200 underline" : "text-blue-600 underline"}
            >
              {part}
            </a>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
}
const COMPOSER_EMOJIS = [
  "😀","😁","😂","🤣","😊","😍","😎","😘",
  "🤔","🙄","😴","😭","😡","🥺","😅","😳",
  "👍","👎","👏","🙏","💪","🤝","🙌","👌",
  "❤️","💔","🔥","✨","⭐","🎉","🎊","🥳",
  "✅","❌","⚠️","💯","💡","📌","📎","🔔",
  "☕","🍕","🍔","🍺","🍾","🎂","🥂","🚀",
];

function ReactionAddButton({ msg, mine, onToggle }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`dm-react-add-${msg.id}`}
          title="Tepki ekle"
          className={[
            "self-center opacity-0 group-hover:opacity-100 transition-opacity",
            "flex h-6 w-6 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-500 shadow-sm hover:bg-zinc-50",
            mine ? "mr-1" : "ml-1 order-first",
          ].join(" ")}
        >
          <Smile className="h-3.5 w-3.5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align={mine ? "end" : "start"}
        className="w-auto p-1.5"
        data-testid={`dm-picker-${msg.id}`}
      >
        <div className="flex gap-0.5">
          {REACTION_SET.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => onToggle(e)}
              data-testid={`dm-reaction-pick-${e}`}
              className="rounded p-1.5 text-lg transition-transform hover:scale-125 hover:bg-zinc-100"
            >
              {e}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Old inline `ReactionRow` removed — replaced by inline chips + floating
// `ReactionAddButton` (WhatsApp-style) rendered next to each bubble.
